import { register } from '../services/AuthService';
import React, { useCallback, useEffect, useState } from 'react';
import { setNToken } from '../../utils/cookie';
import { useTranslations } from '../../localization/useTranslations';
import Icon from '../../../src/common/components/Icon';
import { redirectToCrmLogin } from '../../common/Auth';
import SocialButton from '../../components/buttons/SocialButton/SocialButton';
import Button from '../../components/buttons/SimpleButton/Button';
import { onLoginSuccess } from '../../common/LoggedOutUserActions';
import { getLastVisitedContentUrl } from '../../utils/getLastVisitedContent';

interface RegisterFormData {
  initialEmail: string;
  subscribeToNewsletters: () => void;
  onBackLinkClick: () => void;
  isFormInModal?: boolean;
  previousUrl: string;
}

export default function Register({
  initialEmail,
  onBackLinkClick,
  subscribeToNewsletters,
  isFormInModal,
  previousUrl,
}: RegisterFormData) {
  const [email] = useState(initialEmail);
  const [password, setPassword] = useState('');
  const [registerInProgress, setRegisterInProgress] = useState(false);
  const [focused, setFocused] = useState(false);
  const [privacyText, setPrivacyText] = useState('');

  const [validationResults, setValidationResults] = useState({
    lowerCase: false,
    upperCase: false,
    numberOrSpecialChar: false,
    length: false,
  });

  const [showPassword, setShowPassword] = useState(false);
  const translations = useTranslations();

  const handleFocus = () => setFocused(true);

  const handleBlur = () => {
    setFocused(false);
  };

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const checkForPasswordErrors = useCallback(() => {
    const lowerCaseRegex = /[a-z]/;
    const upperCaseRegex = /[A-Z]/;
    // eslint-disable-next-line
    const numberOrSpecialCharRegex =
      // eslint-disable-next-line
      /[0-9!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/;
    // eslint-disable-next-line
    const lengthRegex = /.{8,}/;

    const lowerCase = lowerCaseRegex.test(password);
    const upperCase = upperCaseRegex.test(password);
    const numberOrSpecialChar = numberOrSpecialCharRegex.test(password);
    const length = lengthRegex.test(password);

    setValidationResults({
      lowerCase,
      upperCase,
      numberOrSpecialChar,
      length,
    });

    if (lowerCase && upperCase && numberOrSpecialChar && length) {
      setRegisterInProgress(false);
    } else {
      setRegisterInProgress(true);
    }
  }, [password]);

  useEffect(() => {
    checkForPasswordErrors();
  }, [checkForPasswordErrors]);

  const [formError, setFormError] = useState(false);

  const handleRegister = async () => {
    if (
      validationResults.lowerCase &&
      validationResults.upperCase &&
      validationResults.numberOrSpecialChar &&
      validationResults.length
    ) {
      setRegisterInProgress(true);
      setFormError(false);
      try {
        const lastVisitedContentUrl = getLastVisitedContentUrl();

        const data = await register({
          email,
          password,
          referer: lastVisitedContentUrl,
        });

        if (data.status === 'ok') {
          if (data.access.token) {
            setNToken(data.access.token);

            const pendingAction = localStorage.getItem('pendingAction');

            if (pendingAction) {
              const { actionType, id } = JSON.parse(pendingAction);
              await onLoginSuccess(actionType, id);
            }

            redirectToCrmLogin();

            if (!isFormInModal) {
              // Redirect the user back to the previous URL
              window.location.href = previousUrl;
            }

            subscribeToNewsletters();
          } else {
            setRegisterInProgress(false);
          }
        } else {
          setRegisterInProgress(false);
        }
      } catch (error) {
        console.error(error);
        setRegisterInProgress(false);
      }
    } else {
      setFormError(true);
      setRegisterInProgress(false);
    }
  };

  useEffect(() => {
    const fetchedPrivacyText = window?.forbes_authentication?.privacyText || '';
    setPrivacyText(fetchedPrivacyText);
  }, []);

  return (
    <>
      <div className="login-modal__form login-modal__form--no-exist">
        <div className="input-group">
          <label className="input-group__label disabled">{translations?.authLabels?.emailLabel || 'Email'}</label>
          <input
            type="email"
            placeholder={translations?.authLabels?.emailPlaceholder || 'Email'}
            value={email}
            disabled
          />

          <span className="input-group__back-link" onClick={onBackLinkClick}>
            {translations?.authLabels?.backLink || 'Back'}
          </span>
        </div>
        <div className={`input-group ${formError ? 'error' : ''}`}>
          <label className={`input-group__label ${focused ? 'focused' : ''}`}>
            {translations?.authLabels?.passLabel || 'Password'}
          </label>
          <div className="password-input">
            <input
              type={showPassword ? 'text' : 'password'}
              placeholder={translations?.authLabels?.passwordPlaceholder || 'Password'}
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              onKeyPress={(e) => {
                if (e.key === 'Enter') {
                  handleRegister();
                }
              }}
              autoFocus
              onFocus={handleFocus}
              onBlur={handleBlur}
            />
            <span className="password-toggle" onClick={togglePasswordVisibility}>
              {showPassword ? <Icon name="eye-off" size="medium" /> : <Icon name="eye-on" size="medium" />}
            </span>
          </div>
        </div>
        <div className="login-modal__live-validation">
          <p className="basic-14">{translations?.authLabels?.passStrong || 'Password strength:'}</p>
          <ul>
            <li id="error-requiredLowerCase" className={`basic-14 ${validationResults.lowerCase ? 'passed' : ''}`}>
              {translations?.authLabels?.oneLowerLetter || 'one lower letter'}
            </li>
            <li id="error-requiredUpperCase" className={`basic-14 ${validationResults.upperCase ? 'passed' : ''}`}>
              {translations?.authLabels?.oneCapitalLetter || 'one capital letter'}
            </li>
            <li
              id="error-requiredNumberOrSpecialCharacter"
              className={`basic-14 ${validationResults.numberOrSpecialChar ? 'passed' : ''}`}
            >
              {translations?.authLabels?.oneNumberOrSpecialChar || 'one number or special character'}
            </li>
            <li id="error-requiredLength" className={`basic-14 ${validationResults.length ? 'passed' : ''}`}>
              {translations?.authLabels?.minCharacters || 'min. 8 characters'}
            </li>
          </ul>
        </div>

        <Button
          className=""
          label={translations?.authLabels?.register || 'Register'}
          type="primary"
          size="large"
          onClick={() => handleRegister()}
          isDisabled={registerInProgress}
          fullWidth={true}
        />
      </div>
      <div className="authentication__bottom">
        <div className="separator">
          <span className="separator__text basic-14">{translations?.authLabels?.or || 'OR'}</span>
          <div className="separator__line"></div>
        </div>
        <div>
          <SocialButton provider="google" />
          {window.country !== 'hu' && <SocialButton provider="facebook" />}
        </div>
        <div className="authentication__privacy-text">
          <div dangerouslySetInnerHTML={{ __html: privacyText }} />
        </div>
      </div>
    </>
  );
}
